import * as THREE from "three";
import { onScreenResize } from "@massivewebgames/screen";
import { BloomEffect, EffectComposer, EffectPass, RenderPass } from "postprocessing";
import {
  CHARACTER,
  DAMAGE,
  PLANT_LOOKS,
  T,
  asteroidPlants,
  clamp,
  plantAt,
  TURRET,
  turretPivot,
  toolOf,
  treeCrown,
  vDist,
  WORLD_GEN,
  lerpAngle,
  wrapAngle,
  outageState,
  type AsteroidBody,
  type AsteroidDef,
  type Outage,
  type BlockKey,
  type CreatureState,
  type DropState,
  type PlayerState,
  type ProjectileState,
  type ShipBody,
  type ItemId,
  type SunDef,
  type Tether,
  type TreeStyle,
  type Vec2,
  type WorldDef,
} from "@dyson/shared";
import { AsteroidView } from "./AsteroidView";
import { CharacterMesh, type CharacterMode } from "./CharacterMesh";
import { GrappleLine, creatureView, type CreatureView } from "./CreatureMesh";
import { Skylife } from "./Skylife";
import { ENGINE_GLOW, bioGlowMaterial, engineGlowMaterial } from "./GridMesh";
import { CRACKS, cracksAt } from "./cracks";
import { Ghost } from "./Ghost";
import { GroundFlames } from "./GroundFlames";
import { ProjectilePool } from "./ProjectilePool";
import { PathLine, type PathView } from "./PathLine";
import { LooseItems } from "./LooseItems";
import { SteamPuffs } from "./Steam";
import { buildShell, setShellCharge } from "./Shell";
import { ShipView, type ShipPose } from "./ShipView";
import { buildSun, lightAt, setSunOutput } from "./SunMesh";
import { Rifts } from "./Rifts";
import { VineViews } from "./Vines";
import { tickLife } from "./AsteroidLife";
import { touchPlants } from "./plantTouch";
import { leafColour } from "./treeArt";

/**
 * What to draw this frame. Positions are already smoothed by the game
 * (prediction blending for us, snapshot interpolation for everyone else), so
 * the renderer places things exactly where it is told.
 */
export interface RenderView {
  ships: Iterable<ShipBody>;
  /** Display pose of each ship. */
  shipPose: Map<string, ShipPose>;
  /** Every player to draw, our own predicted character included. */
  players: Iterable<PlayerState>;
  myPlayerId?: string;
  /** What each player is doing, which picks their animation (default "ground" or "air"), and which way the rock they hold lies in their own frame (see holdDirection). */
  modes: Map<string, { mode: CharacterMode; grip: Vec2 | null }>;
  projectiles: ProjectileState[];
  /** Loose items in view (see DROPS). */
  drops: DropState[];
  /**
   * Build-mode highlight: the hovered tile's corner (grid-local when `shipId`
   * is set, otherwise world) and whether the edit is allowed.
   */
  cursor?: {
    shipId?: string;
    x: number;
    y: number;
    /** Blocks across and up: more than one for a whole piece. */
    w: number;
    h: number;
    ok: boolean;
    /** How far through breaking it, 0..1. */
    progress?: number;
    /** What placing would put there, shown faintly (see Ghost). */
    ghost?: number;
    /** Quarter turns the ghost is turned by (a piece on a rock that turns, see turnAt). */
    turn?: number;
  };
  /**
   * Turn the camera with our character: to the ship's floor while aboard, and
   * to the ground under our feet on an asteroid. False keeps the world upright.
   */
  cameraTurns: boolean;
  /** Creatures in view. */
  creatures: CreatureState[];
  /** Suns flaring or about to, by id: 0..1 through the warning, 1 flaring. */
  flares: Map<string, number>;
  /** The sphere shutting down: seconds since it began (see outageState), or null. */
  shutdown: number | null;
  /** Rifts opening: [x, y, seconds open]. */
  rifts: [number, number, number][];
  /** Asteroid tiles on fire: [asteroidId, index]. */
  groundFires: [string, number][];
  asteroids: Map<string, AsteroidBody>;
  /** Where a drifting rock's grid is to be drawn (see rockOrigin). */
  rockAt: (def: AsteroidDef) => Vec2;
  /** Vines cut through (see VINES). */
  vinesCut: ReadonlySet<string>;
  /** Photo mode: no cursor or names, and the camera pans by `photoPan` (world units) from the character. */
  photo: boolean;
  photoPan: Vec2;
  /** The dotted line ahead of us while we brace, if we are (see PathLine). */
  path: PathView | null;
}

/** The colour of a tree's leaves when they come off it: picked here, or else its leaves' mid tone; none for a bare tree. */
const leavesOf = (style: TreeStyle): readonly [number, number, number] | null => LEAF_COLOURS[style] ?? leafColour(style);
const LEAF_COLOURS: Partial<Record<TreeStyle, readonly [number, number, number]>> = {
  oak: [0.3, 0.6, 0.2],
  pine: [0.16, 0.42, 0.24],
  birch: [0.66, 0.72, 0.26],
  willow: [0.42, 0.66, 0.3],
  lantern: [0.95, 0.72, 0.26],
  frost: [0.72, 0.86, 1.0],
  dusk: [0.56, 0.36, 0.86],
};

/** How zoomed out (camera distance) is "far": ships drawn as silhouettes, other people hidden; and "close": tile seams. */
const ZOOM_FAR = 210;
/** Landing faster than this (blocks/s, feet first) by glow fungus bursts it. */
const SPORE_LANDING = 14;
/** How fast something has to be flying (blocks a second) to knock leaves out of a crown it goes through (see brushLeaves). */
const BRUSH_SPEED = 2;
/** A steampack blasting in a crown knocks leaves out as if it were flying through this fast (blocks a second), however slow it goes. */
const BRUSH_JET = 6;
/** Glow fungus tiles, in every look. */
const GLOW_FUNGI = new Set<number>(PLANT_LOOKS.map((look) => T[`${look.toUpperCase()}_GLOW_FUNGUS` as BlockKey]));
const ZOOM_CLOSE = 48;

/** Height of the shoulder above the feet, where aiming is measured from (see CharacterMesh). */
const SHOULDER_HEIGHT = CHARACTER.height * (1.17 / 1.7);
const ZOOM_MIN = 10;
/** Closer in than this the haze stops thickening, and a crash shakes and name tags show no bigger on screen: all set for the play plane further off. */
const ZOOM_TIGHT = 36;
const ZOOM_MAX = 320;
const ZOOM_DEFAULT = 84;
/*
 * Nothing turns a body but Q/E any more (see movePlayer), so the view has no
 * ground to chase: it is locked to the character, and the easing and step
 * smoothing it used to need are gone with the bumpy screen.
 */

/**
 * Side-view camera looking down -Z at the XY play plane, following the local
 * character. Scroll wheel changes the camera distance (zoom).
 */
export class Renderer {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private readonly gl: THREE.WebGLRenderer;
  /** Scene -> bloom -> screen. Renders in half-float so emissive colours above 1 survive to the bloom pass. */
  private readonly composer: EffectComposer;
  private time = 0;
  private readonly worldGroup = new THREE.Group();
  private readonly asteroidMeshes = new Map<string, AsteroidView>();
  private readonly shipViews = new Map<string, ShipView>();
  private readonly characters = new Map<string, CharacterMesh>();
  private readonly projectiles = new ProjectilePool(512);
  private readonly steam = new SteamPuffs();
  /** Loose items lying about (see DROPS), drawn where the snapshots put them. */
  private readonly loose = new LooseItems();
  private readonly pathLine = new PathLine();
  private readonly cursor: THREE.LineLoop;
  private readonly cracks: THREE.Mesh;
  private readonly ghost = new Ghost();
  private readonly raycaster = new THREE.Raycaster();
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  private readonly tmpV = new THREE.Vector3();
  private world?: WorldDef;
  private zoomTarget = ZOOM_DEFAULT;
  private zoom = ZOOM_DEFAULT;
  /** Camera roll: locked to its target, plus an offset that eases out when the target changes. */
  private camAngle = 0;
  private camOffset = 0;
  private camKey: string | null = null;
  private readonly bloom: EffectPass;
  private readonly creatures = new Map<string, CreatureView>();
  private readonly grapples = new Map<string, GrappleLine>();
  /** Harpoon lines, by ship id. */
  private readonly tethers = new Map<string, GrappleLine>();
  private readonly groundFlames = new GroundFlames();
  private readonly vines = new VineViews();
  /** Spores bursting from puffcaps when someone lands hard by them. */
  private readonly spores = new SteamPuffs(400, [0.55, 0.22, 0.5]);
  /** Jellies and moths round the rocks near the camera, and bits of leaf from gas bags eating. */
  private readonly skylife = new Skylife();
  /** Each player's last step: on the ground or not, and how fast they were falling, to see hard landings. */
  private readonly landing = new Map<string, { grounded: boolean; fall: number }>();
  private readonly suns = new Map<string, THREE.Object3D>();
  /** Suns in an outage now, by id (see NIGHT), and seconds until the rocks' light is next brought in line with them. */
  private outage: Outage | null = null;
  private untilRelight = 0;
  private dimmed = false;
  /** The sphere wall, and how live its seams are (1 by day, 0 while the nearby sun is out; see setShellCharge). */
  private shell: THREE.Object3D | null = null;
  private shellCharge = 1;
  private shellFlicker = 0;
  private readonly rifts = new Rifts();
  /** Camera shake from crashes and hits nearby: how much is left (eases away). */
  private trauma = 0;
  /** Each ship's drawn velocity last frame, to tell how it's speeding up (crew sway with it). */
  private readonly shipMotion = new Map<string, { pos: Vec2; vel: Vec2; accel: Vec2 }>();
  private detail: "far" | "normal" | "close" = "normal";
  private readonly tmpHand = new THREE.Vector3();
  private readonly tmpHook = new THREE.Vector3();

  constructor(private readonly canvas: HTMLCanvasElement) {
    // Antialiasing happens in the composer (multisampling), not the default framebuffer.
    this.gl = new THREE.WebGLRenderer({ canvas, antialias: false, stencil: false, powerPreference: "high-performance" });
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.camera = new THREE.PerspectiveCamera(50, 1, 1, 8000);

    this.composer = new EffectComposer(this.gl, { frameBufferType: THREE.HalfFloatType, multisampling: 4 });
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    // Only things brighter than white (suns, furnaces, cannonballs) cross the threshold and glow.
    const bloom = new BloomEffect({ luminanceThreshold: 1.0, luminanceSmoothing: 0.2, intensity: 1.4, mipmapBlur: true, radius: 0.75 });
    this.bloom = new EffectPass(this.camera, bloom);
    this.composer.addPass(this.bloom);

    this.scene.background = new THREE.Color(0x0a0808);
    // There is air in here, so the far side of the sphere hazes out.
    this.scene.fog = new THREE.FogExp2(0x0a0808, 0.0016);
    // Only the decorative backdrop rocks use lit materials.
    this.scene.add(new THREE.HemisphereLight(0xffe2b8, 0x1a1210, 1.2));
    this.scene.add(this.worldGroup);
    this.scene.add(this.projectiles.mesh);
    this.scene.add(this.steam.mesh);
    this.scene.add(this.pathLine.group);
    this.scene.add(this.loose.group);
    this.scene.add(this.groundFlames.group);
    this.scene.add(this.vines.group);
    this.scene.add(this.spores.mesh);
    this.scene.add(this.skylife.group);
    this.scene.add(this.rifts.group);

    const square = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0.4),
      new THREE.Vector3(1, 0, 0.4),
      new THREE.Vector3(1, 1, 0.4),
      new THREE.Vector3(0, 1, 0.4),
    ]);
    this.cursor = new THREE.LineLoop(square, new THREE.LineBasicMaterial({ color: 0xffffff, depthTest: false }));
    this.cursor.renderOrder = 20;
    this.cursor.visible = false;
    this.scene.add(this.cursor, this.ghost.object);
    // Cracks spreading over the block being broken, drawn just in front of it.
    this.cracks = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1).translate(0.5, 0.5, 0.05),
      new THREE.MeshBasicMaterial({ map: CRACKS[0], transparent: true, depthTest: false }),
    );
    this.cracks.renderOrder = 19;
    this.cursor.add(this.cracks);

    // The screen the stylesheet gives the canvas (see @massivewebgames/screen): drawing at any other size stretches the picture.
    onScreenResize(({ w, h }) => {
      this.gl.setSize(w, h, false);
      this.composer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    });

    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.zoomTarget = clamp(this.zoomTarget * Math.exp(e.deltaY * 0.0012), ZOOM_MIN, ZOOM_MAX);
      },
      { passive: false },
    );
  }

  /** (Re)build all static geometry from a world definition. */
  buildWorld(world: WorldDef, asteroids: AsteroidBody[]): void {
    this.world = world;
    this.worldGroup.clear();
    for (const m of this.asteroidMeshes.values()) m.dispose();
    this.asteroidMeshes.clear();

    this.shell = buildShell(world.worldRadius);
    this.worldGroup.add(this.shell);
    this.suns.clear();
    for (const sun of world.suns) {
      const g = buildSun(sun);
      this.suns.set(sun.id, g);
      this.worldGroup.add(g);
    }
    for (const a of asteroids) this.addAsteroid(a);
  }

  /** Bloom on or off (off for slow graphics cards). */
  setBloom(on: boolean): void {
    this.bloom.enabled = on;
  }

  /** Shake the camera: a crash (`power` in m/s of speed lost) or a hit near `at`, harder the closer and harder. */
  shake(at: Vec2, power: number): void {
    const near = this.listen(at).gain;
    this.trauma = Math.min(1, this.trauma + near * clamp((power - DAMAGE.impactSpeed * 0.5) / 60, 0.05, 0.8));
  }

  /** A world point on screen, in client pixels (possibly off the edges). */
  worldToScreen(p: Vec2): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const v = this.tmpV.set(p.x, p.y, 0).project(this.camera);
    return { x: rect.left + ((v.x + 1) / 2) * rect.width, y: rect.top + ((1 - v.y) / 2) * rect.height };
  }

  get zoomLevel(): number {
    return this.zoom;
  }

  /** Camera distance times `factor` (a pinch), within the same limits as the wheel. */
  zoomBy(factor: number): void {
    this.zoomTarget = clamp(this.zoomTarget * factor, ZOOM_MIN, ZOOM_MAX);
  }

  /** Draw an asteroid, or a structure chunk someone just started building in open space. */
  addAsteroid(a: AsteroidBody): void {
    const suns = this.world?.suns ?? [];
    if (this.asteroidMeshes.has(a.def.id)) return;
    const view = new AsteroidView(a, (x, y) => lightAt(suns, a.def.ox + x, a.def.oy + y));
    this.asteroidMeshes.set(a.def.id, view);
    this.worldGroup.add(view.group);
  }

  /** Call after tile `index` of an asteroid changes. */
  /** An asteroid the client no longer has loaded. */
  removeAsteroid(id: string): void {
    const view = this.asteroidMeshes.get(id);
    if (!view) return;
    this.worldGroup.remove(view.group);
    view.dispose();
    this.asteroidMeshes.delete(id);
  }

  rebuildAsteroid(a: AsteroidBody, indices: number | readonly number[]): void {
    this.asteroidMeshes.get(a.def.id)?.update(indices);
  }

  rebuildShip(body: ShipBody): void {
    this.shipView(body).rebuild(body);
  }

  /**
   * Puff steam from the engines firing on a ship: those whose exhaust points
   * against the way it's being pushed (W fires the downward ones, and so on).
   */
  private blowSteam(sv: ShipView, throttle: Vec2, angle: number, dt: number): void {
    if (!throttle.x && !throttle.y) return;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    for (const e of sv.engines) {
      const firing = (throttle.x > 0 && e.dx < 0) || (throttle.x < 0 && e.dx > 0) || (throttle.y > 0 && e.dy < 0) || (throttle.y < 0 && e.dy > 0);
      // About 10 puffs a second from each.
      if (!firing || Math.random() > dt * 10) continue;
      const at = sv.toWorld(e.x + 0.5 + e.dx * 0.7, e.y + 0.5 + e.dy * 0.7, this.tmpV);
      this.steam.emit(at.x, at.y, e.dx * cos - e.dy * sin, e.dx * sin + e.dy * cos);
    }
  }

  /** Where a harpoon's line is stuck, as drawn now: in a creature (as it's drawn), another ship, or rock. Null if that isn't here. */
  private tetherEnd(t: Tether, view: RenderView): Vec2 | null {
    if (t.creature) {
      const c = this.creatures.get(t.creature)?.group.position;
      return c ? { x: c.x, y: c.y } : t.point;
    }
    if (t.ship) {
      const sv = this.shipViews.get(t.ship);
      if (!sv) return null;
      const p = sv.toWorld(t.point.x, t.point.y, this.tmpV);
      return { x: p.x, y: p.y };
    }
    return this.hookAt(t.point, t.rock, view);
  }

  /** Where a hook is (see hookPoint): `point` itself, or that point of drifting rock `rock`, wherever it's drawn now. Null if the rock isn't here. */
  private hookAt(point: Vec2, rock: string | null | undefined, view: RenderView): Vec2 | null {
    if (!rock) return point;
    const a = view.asteroids.get(rock);
    if (!a) return null;
    const o = view.rockAt(a.def);
    return { x: o.x + point.x, y: o.y + point.y };
  }

  /** A player swings once (placing a block). Digging swings come from `PlayerState.using`. */
  swing(playerId: string): void {
    this.characters.get(playerId)?.swing();
  }

  /** Mouse position (client pixels) to a point on the play plane. */
  screenToWorld(clientX: number, clientY: number): Vec2 {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = this.raycaster.ray.intersectPlane(this.plane, this.tmpV);
    return hit ? { x: hit.x, y: hit.y } : { x: this.camera.position.x, y: this.camera.position.y };
  }

  /**
   * How a sound at a world point should play for the viewer: louder when near
   * the middle of the screen, panned by where it appears on screen (which
   * accounts for the camera turning with a ship).
   */
  listen(p: Vec2): { gain: number; pan: number } {
    const d = Math.hypot(p.x - this.camera.position.x, p.y - this.camera.position.y);
    const range = 60 + this.zoom * 1.5;
    const ndc = this.tmpV.set(p.x, p.y, 0).project(this.camera);
    return { gain: Math.max(0, 1 - d / range), pan: Math.max(-1, Math.min(1, ndc.x)) * 0.8 };
  }

  private shipView(body: ShipBody): ShipView {
    let view = this.shipViews.get(body.state.id);
    if (!view) {
      view = new ShipView();
      this.scene.add(view.group);
      this.shipViews.set(body.state.id, view);
      view.rebuild(body);
      view.setPose(body.state);
    }
    return view;
  }

  update(dt: number, view: RenderView): void {
    this.pathLine.update(view.path);
    const suns = this.world?.suns ?? [];
    this.outage = view.shutdown === null ? null : outageState(view.shutdown);
    this.relight(view, dt);
    this.chargeShell(suns, dt);
    // Zoomed far out, ships are silhouettes and nobody else is drawn; up close, tile seams show.
    this.detail = this.zoom > ZOOM_FAR ? "far" : this.zoom < ZOOM_CLOSE ? "close" : "normal";

    this.updateShips(view, dt);
    this.steam.update(dt);
    this.updateDrops(view, dt);
    for (const av of this.asteroidMeshes.values()) {
      av.place(view.rockAt);
      av.aimTurrets();
    }

    this.updateCreatures(view, dt);
    this.rifts.update(view.rifts, this.time);
    this.rockAt = view.rockAt;
    this.chopLeaves(view, dt);
    this.brushLeaves(view, dt);
    this.skylife.update(dt, this.camera.position, view.asteroids, view.rockAt);
    this.groundFlames.update(view.groundFires, view.asteroids, this.time);
    this.vines.update(this.world, view.vinesCut, view.asteroids, this.time, this.camera.position, this.zoom * 2 + 200);
    this.plantsFeel(view, dt);
    this.updateSuns(view);

    const me = this.updateCharacters(view, dt);
    this.projectiles.update(view.projectiles);
    this.updateCursor(view);

    let myCam = me.cam;
    if (!view.cameraTurns || view.photo) myCam = { angle: view.photo ? this.camAngle : 0, key: view.photo ? this.camKey : null };
    if (me.world) {
      if (view.photo) me.world.set(me.world.x + view.photoPan.x, me.world.y + view.photoPan.y, 0);
      this.followCamera(me.world, myCam.angle, myCam.key, dt);
    }
    // Furnaces flicker; glow bulbs and crystals breathe slowly.
    this.time += dt;
    tickLife(this.time);
    const f = 0.85 + 0.1 * Math.sin(this.time * 23) + 0.05 * Math.sin(this.time * 61);
    engineGlowMaterial.color.copy(ENGINE_GLOW).multiplyScalar(f);
    bioGlowMaterial.color.setScalar(0.8 + 0.2 * Math.sin(this.time * 1.7));

    this.composer.render(dt);
  }

  /** Ships: posed, lit, their exhaust and harpoon lines; the views of ones no longer in view are dropped. */
  private updateShips(view: RenderView, dt: number): void {
    const suns = this.world?.suns ?? [];
    const seenShips = new Set<string>();
    let myFrame: string | null = null;
    for (const p of view.players) if (p.id === view.myPlayerId) myFrame = p.frame;
    for (const body of view.ships) {
      const id = body.state.id;
      seenShips.add(id);
      const sv = this.shipView(body);
      const pose = view.shipPose.get(id) ?? body.state;
      sv.setPose(pose);
      // Only its crew see inside; to everyone else it's just hull.
      sv.setCovered(myFrame !== id);
      sv.setDetail(myFrame === id && this.detail === "far" ? "normal" : this.detail);
      sv.setTurrets(body.state.turrets, body.state.tether?.anchor ?? null);
      sv.setScars(body.state.damage, body.state.fires, this.time);
      sv.setLight(lightAt(suns, sv.group.position.x, sv.group.position.y, this.outage), body.state.pirate);
      sv.setFire(body.state.fuel > 0 && body.state.water > 0);
      const throttle = pose.throttle ?? body.state.throttle;
      sv.setExhaust(throttle);
      this.blowSteam(sv, throttle, pose.angle, dt);
      this.trackMotion(id, pose.position, dt);
      // A harpoon's line out: from the tip of its launcher to what it's stuck in.
      const tether = body.state.tether;
      const hooked = tether && this.tetherEnd(tether, view);
      if (tether && hooked) {
        const pivot = turretPivot(body.grid, tether.anchor);
        const aim = body.state.turrets[tether.anchor]?.aim ?? Math.PI / 2;
        this.lineFor(this.tethers, id).set(sv.toWorld(pivot.x + Math.cos(aim) * TURRET.barrel, pivot.y + Math.sin(aim) * TURRET.barrel, this.tmpHook), hooked);
      } else this.dropLine(this.tethers, id);
    }
    for (const id of this.tethers.keys()) if (!seenShips.has(id)) this.dropLine(this.tethers, id);
    for (const [id, sv] of this.shipViews) {
      if (seenShips.has(id)) continue;
      this.scene.remove(sv.group);
      sv.dispose();
      this.shipViews.delete(id);
      this.shipMotion.delete(id);
    }
  }

  /** A sun going out dims to an ember, and comes back (see NIGHT); one about to flare swells its glow; flaring, it blazes. */
  private updateSuns(view: RenderView): void {
    const suns = this.world?.suns ?? [];
    for (const [id, g] of this.suns) {
      const sun = suns.find((s) => s.id === id);
      if (sun) setSunOutput(g, sun, this.outage?.output ?? 1, this.time);
      const f = view.flares.get(id) ?? 0;
      const pulse = f >= 1 ? 1.6 + 0.15 * Math.sin(this.time * 9) : 1 + f * 0.4 * (0.6 + 0.4 * Math.sin(this.time * (4 + f * 10)));
      g.children[0]?.scale.setScalar(pulse);
    }
  }

  /**
   * Characters, drawn relative to the ship they stand in so they ride along
   * smoothly, with their grapple lines. Returns where our own is (the
   * middle of the body) and what the camera should turn with.
   */
  private updateCharacters(view: RenderView, dt: number): { world?: THREE.Vector3; cam: { angle: number; key: string | null } } {
    let myWorld: THREE.Vector3 | undefined;
    let myCam: { angle: number; key: string | null } = { angle: 0, key: null };
    const head = new THREE.Vector3();
    const seenPlayers = new Set<string>();
    for (const p of view.players) {
      seenPlayers.add(p.id);
      let cm = this.characters.get(p.id);
      if (!cm) {
        cm = new CharacterMesh(p.name, p.look);
        this.scene.add(cm.group);
        this.characters.set(p.id, cm);
      }
      cm.setLook(p.look);
      cm.setGear(p.gear);
      const me = p.id === view.myPlayerId;
      cm.group.visible = me || this.detail !== "far";
      cm.setLabelVisible(!view.photo && this.detail !== "far");
      cm.setLabelScale(Math.min(1, this.zoom / ZOOM_TIGHT));
      const frameView = p.frame ? this.shipViews.get(p.frame) : undefined;
      const ux = -Math.sin(p.up);
      const uy = Math.cos(p.up);
      if (frameView) {
        frameView.toWorld(p.position.x, p.position.y, cm.group.position);
        cm.group.rotation.z = frameView.angle + p.up;
      } else {
        cm.group.position.set(p.position.x, p.position.y, 0);
        cm.group.rotation.z = p.up;
      }
      // The steampack blasting: puffs out of their back, down their body.
      if (p.jetting && Math.random() < dt * 30) {
        const r = cm.group.rotation.z;
        const bx = -Math.sin(r);
        const by = Math.cos(r);
        this.steam.emit(cm.group.position.x + bx * 1.3 - by * p.facing * 0.4, cm.group.position.y + by * 1.3 + bx * p.facing * 0.4, -bx, -by);
      }
      // Walking speed along the ground, which curves round asteroids.
      // The mouse relative to their shoulder, turned into their own frame (however ship and ground tilt them).
      const turn = cm.group.rotation.z;
      const sx = cm.group.position.x - Math.sin(turn) * SHOULDER_HEIGHT;
      const sy = cm.group.position.y + Math.cos(turn) * SHOULDER_HEIGHT;
      const dx = p.aim.x - sx;
      const dy = p.aim.y - sy;
      const aim = Math.hypot(dx, dy) > 0.3 ? { x: dx * Math.cos(turn) + dy * Math.sin(turn), y: -dx * Math.sin(turn) + dy * Math.cos(turn) } : null;
      const doing = view.modes.get(p.id);
      cm.animate(dt, {
        mode: doing?.mode ?? (p.grounded ? "ground" : "air"),
        grip: doing?.grip ?? null,
        facing: p.facing,
        along: p.velocity.x * uy - p.velocity.y * ux,
        rise: p.velocity.x * ux + p.velocity.y * uy,
        grounded: p.grounded,
        aim,
        using: p.using,
        tool: toolOf(p.holding),
        sway: frameView ? this.swayIn(p.frame!, frameView.angle) : 0,
        jetting: !!p.jetting,
      });
      // A grapple line from their middle to where it's hooked (into rock, or a ship, which it follows), or out to its end as it flies.
      const hookShip = p.grappleShip ? this.shipViews.get(p.grappleShip) : undefined;
      const hook = !p.grapple ? null : p.grappleRock ? this.hookAt(p.grapple, p.grappleRock, view) : !p.grappleShip ? p.grapple : hookShip ? hookShip.toWorld(p.grapple.x, p.grapple.y, this.tmpHook) : null;
      const flying = !p.grapple && p.grappleOut != null;
      if (hook || flying) {
        const t = cm.group.rotation.z;
        const hand = this.tmpHand.set(cm.group.position.x - Math.sin(t) * 2, cm.group.position.y + Math.cos(t) * 2, 0);
        const dir = p.grappleDir ?? 0;
        const end = hook ?? { x: hand.x + Math.cos(dir) * p.grappleOut!, y: hand.y + Math.sin(dir) * p.grappleOut! };
        this.lineFor(this.grapples, p.id).set(hand, end);
      } else this.dropLine(this.grapples, p.id);
      if (p.id === view.myPlayerId) {
        // Follow the body's middle, which stays put on screen however the ship or the ground turns.
        const mid = CHARACTER.height / 2;
        if (frameView) frameView.toWorld(p.position.x + ux * mid, p.position.y + uy * mid, head);
        else head.set(p.position.x + ux * mid, p.position.y + uy * mid, 0);
        myWorld = head;
        myCam = frameView ? { angle: frameView.angle, key: p.frame } : { angle: p.up, key: "body" };
      }
    }
    for (const [id, cm] of this.characters) {
      if (seenPlayers.has(id)) continue;
      this.scene.remove(cm.group);
      cm.dispose();
      this.characters.delete(id);
      this.dropLine(this.grapples, id);
    }
    return { world: myWorld, cam: myCam };
  }

  /** The build cursor: the outline of what's under the mouse, the ghost of the block to place, and the cracks of a dig in progress. */
  private updateCursor(view: RenderView): void {
    this.cursor.visible = !!view.cursor && !view.photo;
    this.ghost.show(view.photo ? undefined : view.cursor?.ghost, view.cursor?.ok ?? false, view.cursor?.turn ?? 0);
    if (view.cursor) {
      const ship = view.cursor.shipId ? this.shipViews.get(view.cursor.shipId) : undefined;
      if (ship) ship.toWorld(view.cursor.x, view.cursor.y, this.cursor.position);
      else this.cursor.position.set(view.cursor.x, view.cursor.y, 0);
      this.cursor.rotation.z = ship ? ship.angle : 0;
      this.cursor.scale.set(view.cursor.w, view.cursor.h, 1);
      // The ghost sits just in front of the tiles it would cover.
      this.ghost.object.position.copy(this.cursor.position).setZ(this.cursor.position.z + 0.05);
      this.ghost.object.rotation.z = this.cursor.rotation.z;
      (this.cursor.material as THREE.LineBasicMaterial).color.setHex(view.cursor.ok ? 0xbfe8a0 : 0xd9583b);
      const progress = view.cursor.progress ?? 0;
      this.cracks.visible = progress > 0;
      (this.cracks.material as THREE.MeshBasicMaterial).map = cracksAt(progress);
    }
  }

  /** The line kept in `lines` for `id`, made (and put in the scene) the first time. */
  /**
   * Plants react to whoever passes (see plantTouch): fronds bend, glow plants
   * brighten; a hard landing by a puffcap bursts it into a cloud of spores.
   */
  private plantsFeel(view: RenderView, dt: number): void {
    const movers: [string, { x: number; y: number }][] = [];
    for (const p of view.players) {
      const ship = p.frame ? [...view.ships].find((s) => s.state.id === p.frame) : undefined;
      if (ship) continue;
      const c = { x: p.position.x - Math.sin(p.up) * CHARACTER.height * 0.5, y: p.position.y + Math.cos(p.up) * CHARACTER.height * 0.5 };
      movers.push([p.id, c]);
      // Landing hard: how fast they were going feet first the step before.
      const was = this.landing.get(p.id);
      const fall = -(p.velocity.x * -Math.sin(p.up) + p.velocity.y * Math.cos(p.up));
      if (was && !was.grounded && p.grounded && was.fall > SPORE_LANDING) this.burstSpores(p.position, view.asteroids);
      this.landing.set(p.id, { grounded: p.grounded, fall });
    }
    for (const s of view.ships) {
      const pose = view.shipPose.get(s.state.id) ?? s.state;
      movers.push([s.state.id, { x: pose.position.x + s.grid.w / 2, y: pose.position.y + s.grid.h / 2 }]);
    }
    touchPlants(movers, this.time);
    this.spores.update(dt);
  }

  /** Glow fungi within reach of a hard landing at `at` burst into spores. */
  private burstSpores(at: Vec2, asteroids: Map<string, AsteroidBody>): void {
    for (const a of asteroids.values()) {
      const x0 = Math.floor(at.x - a.def.ox);
      const y0 = Math.floor(at.y - a.def.oy);
      if (x0 < -4 || y0 < -4 || x0 > a.grid.w + 4 || y0 > a.grid.h + 4) continue;
      for (let dy = -4; dy <= 4; dy++) {
        for (let dx = -4; dx <= 4; dx++) {
          const x = x0 + dx;
          const y = y0 + dy;
          if (x < 0 || y < 0 || x >= a.grid.w || y >= a.grid.h || !GLOW_FUNGI.has(a.grid.tiles[y * a.grid.w + x]!) || Math.random() > 0.35) continue;
          const ang = Math.random() * Math.PI * 2;
          this.spores.emit(a.def.ox + x + 0.5, a.def.oy + y + 0.5, Math.cos(ang) * 0.4, Math.sin(ang) * 0.4);
        }
      }
    }
  }

  private lineFor(lines: Map<string, GrappleLine>, id: string): GrappleLine {
    let line = lines.get(id);
    if (!line) {
      line = new GrappleLine(lines === this.tethers ? 0x8a6a3a : undefined);
      lines.set(id, line);
      this.scene.add(line.line);
    }
    return line;
  }

  private dropLine(lines: Map<string, GrappleLine>, id: string): void {
    const line = lines.get(id);
    if (!line) return;
    this.scene.remove(line.line);
    line.line.geometry.dispose();
    lines.delete(id);
  }

  /** Where each rock was last drawn (see RenderView.rockAt), for things that happen between frames (a tree coming down). */
  private rockAt: ((def: AsteroidDef) => Vec2) | null = null;

  /** Tree `plant` on rock `a`'s crown: its middle where it's drawn, which way the tree grows, how big it is, and its leaves' colour. Null for a bare tree: no leaves to drop. */
  private crownOf(a: AsteroidBody, plant: number): { at: Vec2; out: number; size: number; color: readonly [number, number, number] } | null {
    const t = asteroidPlants(a.def)[plant]?.tree;
    const color = t && leavesOf(t.style);
    if (!t || !color) return null;
    const o = this.rockAt?.(a.def) ?? { x: a.def.ox, y: a.def.oy };
    const { up, size } = treeCrown(t.style, t.blocks);
    return { at: { x: o.x + t.x + Math.cos(t.out) * up, y: o.y + t.y + Math.sin(t.out) * up }, out: t.out, size: Math.max(1.5, size), color };
  }

  /** A point somewhere in a crown. */
  private inCrown(c: { at: Vec2; size: number }): Vec2 {
    const a = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random()) * c.size;
    return { x: c.at.x + Math.cos(a) * r, y: c.at.y + Math.sin(a) * r };
  }

  /** Someone chopping at a standing tree's trunk shakes leaves down out of its crown. */
  private chopLeaves(view: RenderView, dt: number): void {
    for (const p of view.players) {
      if (!p.using || p.frame || vDist(p.position, p.aim) > CHARACTER.reach + 4) continue;
      for (const a of view.asteroids.values()) {
        const x = Math.floor(p.aim.x - a.def.ox);
        const y = Math.floor(p.aim.y - a.def.oy);
        if (x < 0 || y < 0 || x >= a.grid.w || y >= a.grid.h) continue;
        const i = y * a.grid.w + x;
        const plant = plantAt(a.def, i);
        if (plant === null || a.grid.tiles[i] === T.EMPTY) break;
        const crown = this.crownOf(a, plant);
        if (!crown) break;
        const down = { x: -Math.cos(crown.out), y: -Math.sin(crown.out) };
        for (let k = 0; k < 3; k++) {
          if (Math.random() > dt * 3) continue;
          const at = this.inCrown(crown);
          const side = (Math.random() - 0.5) * 1.2;
          this.skylife.leaf(at.x, at.y, down.x * 0.6 - down.y * side, down.y * 0.6 + down.x * side, { down, life: 3, size: 0.45 + Math.random() * 0.2, color: crown.color });
        }
        break;
      }
    }
  }

  /**
   * Something flying through a standing tree's crown (a ship, or someone
   * on their steampack) knocks leaves loose, the faster the more; a
   * steampack blasting shakes them loose even hovering. They're carried a
   * little way along with it, then float off (see trees.md, "Falling leaves").
   */
  private brushLeaves(view: RenderView, dt: number): void {
    const movers: { at: Vec2; v: Vec2; r: number; jet?: boolean }[] = [];
    for (const p of view.players) if (!p.frame && !p.grounded) movers.push({ at: p.position, v: p.velocity, r: 0.6, jet: !!p.jetting });
    for (const s of view.ships) {
      const pose = view.shipPose.get(s.state.id) ?? s.state;
      movers.push({ at: { x: pose.position.x + s.grid.w / 2, y: pose.position.y + s.grid.h / 2 }, v: s.state.velocity, r: Math.min(8, Math.hypot(s.grid.w, s.grid.h) / 2) });
    }
    for (const m of movers) {
      const speed = Math.hypot(m.v.x, m.v.y);
      if (speed < BRUSH_SPEED && !m.jet) continue;
      for (const a of view.asteroids.values()) {
        const o = view.rockAt(a.def);
        if (m.at.x < o.x - 70 || m.at.y < o.y - 70 || m.at.x > o.x + a.grid.w + 70 || m.at.y > o.y + a.grid.h + 70) continue;
        asteroidPlants(a.def).forEach((p, plant) => {
          if (!p.tree || !p.tiles.some((i, k) => a.grid.tiles[i] === p.grown[k])) return;
          const crown = this.crownOf(a, plant);
          if (!crown || vDist(m.at, crown.at) > crown.size + m.r) return;
          // Leaves a second: more the faster it goes and the bigger it is.
          const want = dt * Math.min(30, Math.max(speed, m.jet ? BRUSH_JET : 0) * 0.8 * (1 + m.r));
          for (let n = Math.floor(want) + (Math.random() < want % 1 ? 1 : 0); n > 0; n--) {
            // From where it's pushing through: round it, inside the crown.
            for (let tries = 0; tries < 4; tries++) {
              const ang = Math.random() * Math.PI * 2;
              const d = Math.sqrt(Math.random()) * (m.r + 1.5);
              const at = { x: m.at.x + Math.cos(ang) * d, y: m.at.y + Math.sin(ang) * d };
              if (vDist(at, crown.at) > crown.size) continue;
              const spread = 1 + Math.random() * 1.5;
              this.skylife.leaf(at.x, at.y, m.v.x * 0.35 + Math.cos(ang) * spread, m.v.y * 0.35 + Math.sin(ang) * spread, {
                life: 3 + Math.random() * 2,
                size: 0.45 + Math.random() * 0.3,
                color: crown.color,
              });
              break;
            }
          }
        });
      }
    }
  }

  /** Tree `plant` on rock `a` has come down: its leaves fly off, most floating away, some falling. */
  treeFelled(a: AsteroidBody, plant: number): void {
    const crown = this.crownOf(a, plant);
    if (!crown) return;
    const down = { x: -Math.cos(crown.out), y: -Math.sin(crown.out) };
    const n = Math.round(Math.min(60, Math.max(18, crown.size * 8)));
    for (let k = 0; k < n; k++) {
      const at = this.inCrown(crown);
      const ang = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 3.5;
      const falls = Math.random() < 0.35;
      this.skylife.leaf(at.x, at.y, Math.cos(ang) * speed + Math.cos(crown.out), Math.sin(ang) * speed + Math.sin(crown.out), {
        down: falls ? down : null,
        life: falls ? 3 : 3.5 + Math.random() * 2,
        size: 0.45 + Math.random() * 0.3,
        color: crown.color,
      });
    }
  }

  /**
   * The shell's seams are live while the sun near the camera is lit (see
   * setShellCharge): flickering in patches as it falters, dying as it dims,
   * coming back as it relights. Eased, so nothing snaps.
   */
  private chargeShell(suns: readonly SunDef[], dt: number): void {
    if (!this.shell) return;
    const { x, y } = this.camera.position;
    let near: SunDef | null = null;
    let nearGap: number = WORLD_GEN.life.dark;
    for (const sun of suns) {
      const gap = Math.hypot(x - sun.position.x, y - sun.position.y) - sun.radius;
      if (this.outage && gap < nearGap) [near, nearGap] = [sun, gap];
    }
    const o = near && this.outage;
    const charge = !o ? 1 : o.phase === "falter" ? 1 : o.phase === "dimming" ? 1 - o.u : o.phase === "out" ? 0 : o.u;
    const flicker = o?.phase === "falter" ? 0.4 + 0.6 * o.u : 0;
    const ease = Math.min(1, dt * 3);
    this.shellCharge += (charge - this.shellCharge) * ease;
    this.shellFlicker += (flicker - this.shellFlicker) * ease;
    setShellCharge(this.shell, {
      charge: this.shellCharge,
      flicker: this.shellFlicker,
      sun: near && { x: near.position.x, y: near.position.y, radius: near.radius, reach: WORLD_GEN.life.dark },
      time: this.time,
    });
  }

  /**
   * Bring each rock's light (baked in: see AsteroidView.setSun) in line
   * with the suns, a few times a second while any sun's in an outage, and
   * once more when the last one's over.
   */
  private relight(view: RenderView, dt: number): void {
    this.untilRelight -= dt;
    if (this.untilRelight > 0 || (!this.outage && !this.dimmed)) return;
    this.untilRelight = 0.25;
    this.dimmed = !!this.outage;
    const suns = this.world?.suns ?? [];
    for (const [id, av] of this.asteroidMeshes) {
      const a = view.asteroids.get(id);
      if (!a) continue;
      const x = a.def.ox + a.grid.w / 2;
      const y = a.def.oy + a.grid.h / 2;
      av.setSun(this.dimmed ? lightAt(suns, x, y, this.outage) / Math.max(0.05, lightAt(suns, x, y)) : 1);
    }
  }

  /** Creatures by a rock (see CreatureState.rock) are drawn from where that rock is drawn, so they move with it. */
  private updateCreatures(view: RenderView, dt: number): void {
    const seen = new Set<string>();
    const suns = this.world?.suns ?? [];
    for (const c of view.creatures) {
      const a = c.rock ? view.asteroids.get(c.rock) : undefined;
      const origin = a ? view.rockAt(a.def) : { x: 0, y: 0 };
      const local = a && c.local ? c.local : c.position;
      seen.add(c.id);
      let m = this.creatures.get(c.id);
      if (!m) {
        m = creatureView(c.kind, c.id);
        this.creatures.set(c.id, m);
        this.scene.add(m.group);
      }
      const at = m.group.position;
      m.update(c, origin, local, lightAt(suns, at.x, at.y, this.outage), dt);
      // A gas bag eating drops bits of leaf now and then.
      if (c.kind === "gasbag" && c.eating && Math.random() < dt * 3) this.skylife.leaf(at.x + (Math.random() - 0.5) * 3, at.y + (Math.random() - 0.5) * 2, (Math.random() - 0.5) * 1.5, -0.5 - Math.random() * 1.5);
    }
    for (const [id, m] of this.creatures) {
      if (seen.has(id)) continue;
      this.scene.remove(m.group);
      m.dispose();
      this.creatures.delete(id);
    }
  }

  /**
   * Loose items (see DROPS): each pile is drawn in its own frame, through
   * the ship it's aboard if it is aboard one, so it stays put on the deck
   * however the ship is turned.
   */
  private updateDrops(view: RenderView, dt: number): void {
    this.loose.update(dt, view.drops, (d, x, y, out) => {
      const frame = d.frame ? this.shipViews.get(d.frame) : undefined;
      if (frame) frame.toWorld(x, y, out);
      else out.set(x, y, 0);
      out.z = 0.6;
    });
  }

  /** Follow how a ship is drawn moving, to tell how hard it's speeding up. */
  private trackMotion(id: string, pos: Vec2, dt: number): void {
    const m = this.shipMotion.get(id);
    if (!m || dt <= 0) {
      this.shipMotion.set(id, { pos: { ...pos }, vel: { x: 0, y: 0 }, accel: { x: 0, y: 0 } });
      return;
    }
    const vx = (pos.x - m.pos.x) / dt;
    const vy = (pos.y - m.pos.y) / dt;
    // Teleports and snaps aren't acceleration.
    const jump = Math.hypot(vx - m.vel.x, vy - m.vel.y) > 200;
    const k = 1 - Math.exp(-dt * 6);
    m.accel.x += ((jump ? 0 : (vx - m.vel.x) / dt) - m.accel.x) * k;
    m.accel.y += ((jump ? 0 : (vy - m.vel.y) / dt) - m.accel.y) * k;
    m.vel = { x: vx, y: vy };
    m.pos = { ...pos };
  }

  /** How far crew lean (radians) as their ship speeds up along its own X: thrown back, then forward as it slows. */
  private swayIn(shipId: string, angle: number): number {
    const a = this.shipMotion.get(shipId)?.accel;
    if (!a) return 0;
    const along = a.x * Math.cos(angle) + a.y * Math.sin(angle);
    return -along * 0.012;
  }

  /**
   * Locked to the character (which is already smooth). The camera rolls to
   * `angle` — the ship's floor aboard one, else however the body is turned,
   * which only Q/E changes; when what it follows changes (boarding, leaving,
   * switching mode) it eases over instead of snapping. Crashes shake it.
   */
  /** Which way the top of the screen faces in the world (radians, 0 is +y): the camera's roll, without the shake. */
  get viewAngle(): number {
    return this.camAngle;
  }

  private followCamera(target: THREE.Vector3, angle: number, key: string | null, dt: number): void {
    this.zoom += (this.zoomTarget - this.zoom) * (1 - Math.exp(-dt * 6));
    // The sphere hazes with distance; the haze thins as you zoom out so the play plane stays clear and the far side still fades.
    (this.scene.fog as THREE.FogExp2).density = 0.0016 * Math.pow(ZOOM_DEFAULT / Math.max(this.zoom, ZOOM_TIGHT), 0.8);
    if (key !== this.camKey) {
      this.camOffset = wrapAngle(this.camAngle - angle);
      this.camKey = key;
    }
    this.camOffset = lerpAngle(this.camOffset, 0, 1 - Math.exp(-dt * 4));
    this.camAngle = angle + this.camOffset;
    const ux = -Math.sin(this.camAngle);
    const uy = Math.cos(this.camAngle);
    this.trauma = Math.max(0, this.trauma - dt * 1.4);
    const s = this.trauma * this.trauma * Math.min(1, this.zoom / ZOOM_TIGHT);
    const jx = s * (Math.sin(this.time * 47) + Math.sin(this.time * 31)) * 0.9;
    const jy = s * (Math.sin(this.time * 53 + 1) + Math.sin(this.time * 29)) * 0.9;
    const roll = this.camAngle + s * Math.sin(this.time * 37) * 0.03;
    this.camera.up.set(-Math.sin(roll), Math.cos(roll), 0);
    const cx = target.x + jx;
    const cy = target.y + jy;
    this.camera.position.set(cx, cy, this.zoom);
    this.camera.lookAt(cx, cy, 0);
  }
}
